import { ClassName, Theme } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import EmployeeSummaryTable, {
  IEmployeeSummaryRowInfo,
} from 'components/domain/member/EmployeeSummaryTable/EmployeeSummaryTable';
import TimesheetsActionHeader from 'containers/timesheets/TimesheetsActionHeader/TimesheetsActionHeader';
import TimesheetsHeader from 'containers/timesheets/TimesheetsHeader/TimesheetsHeader';
import TimesheetsMemberActionBar from 'containers/timesheets/action-bar/TimesheetsMemberActionBar/TimesheetsMemberActionBar';
import useTimesheetsRowHeight from 'containers/timesheets/hooks/useTimesheetsRowHeight/useTimesheetsRowHeight';
import { useOpenable, useOrganization, useReduxSelector, useTimesheetsGraylog } from 'hooks';
import useIsPro from 'hooks/store/useIsPro';
import { IUseTimeRangePayload } from 'hooks/utils/useTimeRange';
import { Nullable } from 'types/util/Nullable';
import { TimesheetsTypes } from 'utils/constants/graylogActionTypes';
import { convertTimesheetSummaryRowsToCsv } from '../utils/utils';
import { downloadText } from 'utils/fileUtils';
import useBrandTitle from 'hooks/meta/useBrandTitle';
import { t } from 'i18next';
import { isoTimeStampLocal } from 'utils/dateUtils';
import { useToastOpen } from 'contexts/ToastContext';
import { useLoader } from 'contexts/LoaderContext';
import useMemberGraphAggregates from 'hooks/aggregates/useMemberGraphAggregates';
import { TimesheetView, updateCheckedMemberIds } from 'store/timesheets/Timesheets';
import { useDispatch } from 'react-redux';
import { isNil } from 'lodash';
import { ITimeActionsFormData } from 'components/domain/time-entry/time-actions-form/hooks/useTimeActionsForm';
import { ITimeOffFormData } from 'components/domain/time-off/form/CreateTimeOffForm/CreateTimeOffForm';
import TimesheetsPrintDialog from 'containers/timesheets/TimesheetsPrintDialog/TimesheetsPrintDialog';
import RowSettingsDialog from 'components/foundation/dialogs/RowSettingsDialog/RowSettingsDialog';
import TimeActionsFormDialog from 'components/domain/time-entry/time-actions-form/TimeActionsFormDialog/TimeActionsFormDialog';
import TimeOffFormDialog from 'components/domain/time-off/form/TimeOffFormDialog/TimeOffFormDialog';
import ProFeatureDialog from 'components/domain/account/ProFeatureDialog/ProFeatureDialog';
import TableColumnsDialog from 'components/foundation/table/TableColumnsDialog/TableColumnsDialog';
import TimesheetsColumnsForm from 'containers/timesheets/TimesheetsColumnsForm/TimesheetsColumnsForm';
import useTimesheetSummaryData from 'components/domain/member/EmployeeSummaryTable/hooks/useTimesheetsSummaryData/useTimesheetsSummaryData';
import useTimesheetsQueryParams from 'containers/timesheets/hooks/useTimesheetsQueryParams';
import { useMemo, useRef } from 'react';
import { MemberPermissions } from '__generated__/graphql';
import { Panel } from 'components';
import useTimesheetsAnalytics from 'containers/timesheets/hooks/useTimesheetsAnalytics';

export interface ITimesheetSummaryProps extends IUseTimeRangePayload {
  permission: MemberPermissions;
  className?: ClassName;
}

export default function TimesheetSummary({
  permission,
  forward,
  back,
  timeRange,
  timeRangeType,
  forwardEnabled,
  backEnabled,
  className,
}: ITimesheetSummaryProps) {
  const scroller = useRef<Nullable<HTMLDivElement>>(null);

  const { archivedStatus, positionId, memberId, memberGroupId } = useTimesheetsQueryParams();
  const { data, isLoading, isError, sortBy, sortIsDirty, sortDir, handleSort, refreshDataForMembers, forceLoadAll } =
    useTimesheetSummaryData({
      scroller: scroller.current,
      timeRange,
      timeRangeType,
      memberId,
      positionId,
      memberGroupId,
      archivedStatus: archivedStatus ?? 'unarchived',
      memberPermission: permission,
    });

  const organization = useOrganization();
  const brand = useBrandTitle();

  const { open: openLoader, close: closeLoader } = useLoader();
  const openToast = useToastOpen();

  const timeEntryDialogDetails = useOpenable();
  const timeOffDialogDetails = useOpenable();
  const columnDialogDetails = useOpenable();
  const proUpgradeDialogDetails = useOpenable();
  const printDetails = useOpenable();
  const rowSettingsDialog = useOpenable();

  const userEvents = useTimesheetsGraylog();
  const isPro = useIsPro();

  const dispatch = useDispatch();
  const analytics = useTimesheetsAnalytics();

  const [rowHeight, setRowHeight] = useTimesheetsRowHeight();

  const checkedMemberIds = useReduxSelector((state) => state.timesheet.checkedMemberIds);

  function onPrint() {
    printDetails.open();
    userEvents.events(TimesheetsTypes.events.action_type.PRINT);
  }

  async function handlePrint() {
    printDetails.close();
    openLoader(t('Loading...'));
    analytics.timesheetsSummaryPrinted();
    try {
      await forceLoadAll();

      setTimeout(() => {
        window.print();
      }, 500);
    } catch (e) {
      openToast({ theme: Theme.DANGER, label: t('Something went wrong loading your data.') });
    } finally {
      closeLoader();
    }
  }

  async function onExport() {
    if (!isPro) {
      proUpgradeDialogDetails.open();
      return;
    }

    userEvents.events(TimesheetsTypes.events.action_type.EXPORT);
    openLoader(t('Loading...'));
    analytics.timesheetsSummaryExported();
    try {
      const rows = await forceLoadAll();

      if (rows.length !== 0) {
        const csv = convertTimesheetSummaryRowsToCsv(rows, organization);
        downloadText(csv, `${brand}-${t('timesheets-summary')}-${isoTimeStampLocal()}.csv`);
      } else {
        openToast({ theme: Theme.DANGER, label: t('There is no data to export.') });
      }
    } catch (e) {
      openToast({ theme: Theme.DANGER, label: t('Something went wrong.') });
    } finally {
      closeLoader();
    }
  }

  const memberIds = useMemo(() => (memberId ? [memberId] : null), [memberId]);

  const {
    data: graphAggregates,
    error: graphAggregateError,
    refetchData: refetchAggregates,
  } = useMemberGraphAggregates(timeRange, memberIds, null, null, null, memberGroupId, 2, archivedStatus);

  function onCheck(rows: IEmployeeSummaryRowInfo[]) {
    // TODO: does it make sense to do this filter even though archived status is settable now?
    dispatch(updateCheckedMemberIds(rows.filter((item) => isNil(item.member.archivedOn)).map((item) => item.id)));
  }

  function onTimeEntryFormDataSubmit(formData: ITimeActionsFormData) {
    onDataChange(formData.members);
    timeEntryDialogDetails.close();
  }

  function onTimeOffFormDataSubmit(formData: ITimeOffFormData | undefined) {
    if (formData) {
      onDataChange(formData.members);
    }
    timeOffDialogDetails.close();
  }

  async function onCheckAll(checked: boolean) {
    if (checked) {
      try {
        openLoader(t('Loading...'));
        const all = await forceLoadAll();
        dispatch(updateCheckedMemberIds(all.filter((item) => isNil(item.member.archivedOn)).map((item) => item.id)));
      } catch (e) {
        openToast({ theme: Theme.DANGER, label: t('Something went wrong loading your data') });
      } finally {
        closeLoader();
      }
    } else {
      dispatch(updateCheckedMemberIds([]));
    }
  }

  function onDataChange(memberIds: string[]) {
    refreshDataForMembers(memberIds);
    refetchAggregates();
  }

  const classes = classNames('timesheet-summary', className);

  return (
    <Panel className={classes}>
      <TimesheetsActionHeader
        timeRange={timeRange}
        timeRangeType={timeRangeType}
        onTimeRangeForward={forward}
        onTimeRangeBackward={back}
        forwardDisabled={!forwardEnabled()}
        backwardDisabled={!backEnabled()}
        onTimeEntryAdd={timeEntryDialogDetails.open}
        onTimeOffAdd={timeOffDialogDetails.open}
        onColumnsClick={columnDialogDetails.open}
        onExport={onExport}
        onPrint={onPrint}
        onRowHeightClick={rowSettingsDialog.open}
        actionBar={
          <TimesheetsMemberActionBar
            onTimeEntryFormSubmit={onTimeEntryFormDataSubmit}
            onTimeOffFormSubmit={onTimeOffFormDataSubmit}
          />
        }
      />

      <div ref={scroller} className="overflow-y-auto full-height">
        <TimesheetsHeader
          className="m-6 no-print"
          timeRange={timeRange}
          graphData={graphAggregates}
          aggregateError={graphAggregateError?.name ?? null}
          showDecimalFormat={false}
        />

        <EmployeeSummaryTable
          data={data}
          timeRange={timeRange}
          timeRangeType={timeRangeType}
          onDataChange={onDataChange}
          checkedItems={checkedMemberIds}
          onCheckAll={onCheckAll}
          onCheck={onCheck}
          isError={isError}
          isLoading={isLoading}
          sortIsDirty={sortIsDirty}
          sortDir={sortDir}
          sortBy={sortBy}
          handleSort={handleSort}
          rowHeight={rowHeight}
        />
      </div>

      <TimeActionsFormDialog
        type={'add'}
        isOpen={timeEntryDialogDetails.isOpen}
        onClose={timeEntryDialogDetails.close}
        onSubmit={onTimeEntryFormDataSubmit}
        onDelete={onTimeEntryFormDataSubmit}
      />

      <TimeOffFormDialog
        isOpen={timeOffDialogDetails.isOpen}
        onClose={timeOffDialogDetails.close}
        onSubmit={onTimeOffFormDataSubmit}
      />

      <RowSettingsDialog
        isOpen={rowSettingsDialog.isOpen}
        onClose={rowSettingsDialog.close}
        initialValue={rowHeight}
        onSubmit={setRowHeight}
      />

      <TableColumnsDialog
        isOpen={columnDialogDetails.isOpen}
        onClose={columnDialogDetails.close}
        columnsForm={
          <TimesheetsColumnsForm timesheetView={TimesheetView.SUMMARY} onClose={columnDialogDetails.close} />
        }
      />
      <ProFeatureDialog isOpen={proUpgradeDialogDetails.isOpen} onClose={proUpgradeDialogDetails.close} />
      <TimesheetsPrintDialog isOpen={printDetails.isOpen} onClose={printDetails.close} onPrint={handlePrint} />
    </Panel>
  );
}
